import jwt_decode from 'jwt-decode';
import { WEB_SECTION_ID } from './constants';
import { getQueryParams } from './functional';
import {
  getCurrentJwt,
  getEncryptedUDLData,
  getPrinterInfo,
  getRawUDLData,
  setEncryptedUDLData,
  setRawUDLData
} from './persistData';
import { encrypt } from './request';

export const injectUDLTags = locale => {
  document.getElementsByTagName('html')[0].setAttribute('lang', locale);

  const countryTag = document.createElement('meta');
  countryTag.setAttribute('name', 'target_country');
  countryTag.setAttribute('content', `${locale?.split(/-|_/)[1]}`);
  document.head.appendChild(countryTag);

  const webSectionTag = document.createElement('meta');
  webSectionTag.setAttribute('name', 'web_section_id');
  webSectionTag.setAttribute('content', WEB_SECTION_ID);
  document.head.appendChild(webSectionTag);
};

export const injectUDLScript = () => {
  const udlString = `(function(w,d,s,l,i,o){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var
    f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='//www.googletagmanager.com/gtm.js?id='+i+dl;j.onload=o;f.parentNode.insertBefore(j,f);})(window,document,
    'script','dataLayer','GTM-MZXB4R4');
    `;

  const script = document.createElement('script');
  script.setAttribute('type', 'text/javascript');
  script.setAttribute('charset', 'UTF-8');
  script.setAttribute('id', 'udl-script');
  script.text = udlString;
  document.head.appendChild(script);
};

export const handlePageViewTracking = locale => {
  injectUDLTags(locale);
  trackPageViewUDLEvent();
};

export const encryptData = data => {
  return encrypt(data);
};

export const getOtherPageInfo = () => {
  const queryParams = getQueryParams();
  const rawPageInfo = {};

  if (queryParams.appInstanceId) {
    rawPageInfo.appInstanceID = queryParams.appInstanceId;
  }

  const printerInfo = getPrinterInfo();
  if (printerInfo.device_uuid) {
    rawPageInfo.deviceID = printerInfo.device_uuid;
  }
  if (printerInfo.product_number) {
    rawPageInfo.product_number = printerInfo.product_number;
  }
  if(printerInfo.printer_sessionId) {
    rawPageInfo.printer_sessionId = printerInfo.printer_sessionId;
  }

  const jwt = getCurrentJwt();
  if (jwt) {
    const decodedJWT = jwt_decode(jwt);
    rawPageInfo.userID = decodedJWT.sub;
    rawPageInfo.companyID = decodedJWT.fq_tenant_id;
  }
  return rawPageInfo;
};

const getEncryptedOtherPageInfo = async () => {
  try {
    const previousUDLData = getRawUDLData();
    const currentUDLData = getOtherPageInfo();
    if (!currentUDLData.deviceID && previousUDLData.deviceID) {
      previousUDLData.deviceID = currentUDLData.deviceID;
      const encryptedData = getEncryptedUDLData();
      delete encryptedData.deviceID;
      setEncryptedUDLData(encryptedData);
    }
    if (JSON.stringify(previousUDLData) !== JSON.stringify(currentUDLData)) {
      setRawUDLData(currentUDLData);
      const encryptedData = await encryptData(currentUDLData);
      delete encryptedData.data.date_encrypted;
      setEncryptedUDLData(encryptedData.data);
      return encryptedData.data;
    }
    return getEncryptedUDLData();
  } catch (error) {
    return {};
  }
};

const createPromotionData = tiles => {
  const promotionData = {
    promoView: {
      promotions: []
    }
  };
  if (!Array.isArray(tiles)) {
    tiles = new Array(tiles);
  }
  tiles.forEach(tile => {
    const promotion = {
      id: tile.engagementId
    };
    if (tile.analyticsData) {
      promotion.name = tile.analyticsData.name;
      promotion.creative = tile.analyticsData.createdDate;
    }
    promotion.position = tile.position;
    promotionData.promoView.promotions.push(promotion);
  });
  return promotionData;
};

//e_pageview on cec load
//e_promotionImpression for tile seen by user
//e_promotionClick for tile clicked
//e_linkClick for tile dismissal, showall
//e_internal for event listeners from app
//e_pageview for interstitial screen
//e_linkClick for button clicks, dismiss screen
export const trackPageViewUDLEvent = async (tiles, isFullScreen) => {
  const encryptedOtherPageInfo = await getEncryptedOtherPageInfo();
  const pageViewEvent = {
    event: 'e_pageView',
    pageNameL5: 'CEC',
    ...encryptedOtherPageInfo
  };
  if (isFullScreen) {
    pageViewEvent.pageNameL6 = 'fullScreen';
  }
  pageViewEvent.ecommerce = createPromotionData(tiles);
  window.dataLayer && window.dataLayer.push(pageViewEvent);
};

export const trackAppEventListenerUDLEvent = async (label, value) => {
  const encryptedOtherPageInfo = await getEncryptedOtherPageInfo();
  const listenerEvent = {
    event: 'e_internal',
    action: 'appEventListener',
    label,
    ...encryptedOtherPageInfo
  };
  if (typeof value !== 'undefined') {
    listenerEvent.isLoggedIn = value;
  }
  window.dataLayer && window.dataLayer.push(listenerEvent);
};

export const trackTileViewUDLEvent = async tiles => {
  const encryptedOtherPageInfo = await getEncryptedOtherPageInfo();
  const tileViewEvent = {
    event: 'e_promotionImpression',
    ...encryptedOtherPageInfo
  };
  tileViewEvent.ecommerce = createPromotionData(tiles);
  window.dataLayer && window.dataLayer.push(tileViewEvent);
};

export const trackTileClickedUDLEvent = async tile => {
  const encryptedOtherPageInfo = await getEncryptedOtherPageInfo();
  const tileClickEvent = {
    event: 'e_promotionClick',
    ...encryptedOtherPageInfo
  };
  tileClickEvent.ecommerce = createPromotionData(tile);
  window.dataLayer && window.dataLayer.push(tileClickEvent);
};

export const trackTileDismissalEvent = tile => {
  trackLinkClick('tile_dismiss', tile.analyticsData.name);
};

export const trackLinkClick = async (placement, name) => {
  const encryptedOtherPageInfo = await getEncryptedOtherPageInfo();
  const clickEvent = {
    event: 'e_linkClick',
    linkPlacement: placement,
    linkID: name,
    ...encryptedOtherPageInfo
  };
  window.dataLayer && window.dataLayer.push(clickEvent);
};

export const getTilesSeenFromStorage = () => {
  const tilesSeen = sessionStorage.getItem('tilesSeen');
  return tilesSeen ? tilesSeen.split(',') : [];
};

export const setTilesSeenToStorage = tiles => {
  const tilesSeen = getTilesSeenFromStorage();
  const newTilesSeen = tiles.map(tile => tile.engagementId);
  sessionStorage.setItem('tilesSeen', [...tilesSeen, ...newTilesSeen].toString());
};
